import React, {useEffect, useState} from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Divider,
  TableContainer,
  ModalFooter,
  useDisclosure,
  Checkbox,
  Tfoot,
  Icon,
  Center,
  Input,
  Text,
  Spinner,
  Button,
  Stack,
  Flex,
} from '@chakra-ui/react';
import {CustomCheckbox, CustomInput} from '../../components/General/FormInputs';
import BoxCard from '../../components/General/BoxCard';
import {useTitle} from '../../components/General/Layout';
import {FaTrashAlt, FaPlusSquare, FaChevronUp, FaChevronDown} from 'react-icons/fa';
import {get, post, postDelete} from '../../service/ApiService';
import {Arranger, Survey} from '../../service/Urls';
import {isAdmin} from '../../helpers/localStorage';
import ErrorHelper from '../../service/ErrorHelper';
import {formatDate} from '../../helpers/Functions';
import {useQueryClient, useQuery} from 'react-query';
import {debounce} from 'lodash';
import Pagination from '../../components/General/Pagination';
import {useForm, useFieldArray} from 'react-hook-form';
import ClientSelectInput from '../../components/Client/ClientSelectInput';
import ServiceTypes from '../../constants/ServiceTypes';
import SurveyTableModal from '../../components/Surveys/SurveyTableModal';

const PAGE_SIZE = 8;
const DEBOUNCE_TIME = 600;

export default function ArrangerMaintenance() {
  const [selectedArrangerId, setSelectedArrangerId] = useState<string | null>(sessionStorage.getItem('arranger-selectedArrangerId') ?? null);
  const [toastResponse, setToastResponse] = useState(undefined);
  const [isSalesMan, setIsSalesMan] = useState(true);
  const [_, setTitle] = useTitle('Arranger Maintenance');

  const [page, setPage] = useState(parseInt(sessionStorage.getItem('arranger-page') ?? '1'));

  // Search State
  const [clientId, setClientId] = useState(sessionStorage.getItem('arranger-clientId') ?? '');
  const [arId, setArId] = useState(sessionStorage.getItem('arranger-arId') ?? '');
  const [arName, setArName] = useState(sessionStorage.getItem('arranger-arName') ?? '');
  const [dateAdded, setDateAdded] = useState(sessionStorage.getItem('arranger-dateAdded') ?? '');
  const [lastUpdated, setLastUpdated] = useState(sessionStorage.getItem('arranger-lastUpdated') ?? '');

  // Order State
  const [order, setOrder] = useState('');
  const [isDesc, setIsDesc] = useState(false);

  const queryClient = useQueryClient();

  const {isOpen, onOpen, onClose} = useDisclosure();
  const {
    isOpen: isSurveysOpen,
    onOpen: onSurveysOpen,
    onClose: onSurveysClose,
  } = useDisclosure({defaultIsOpen: !!sessionStorage.getItem('arranger-survey-open')});

  const {data: arrangers, isFetching} = useQuery(
    ['arrangers', page, clientId, arId, arName, dateAdded, lastUpdated, order, isDesc],
    async () => {
      return get(
        `${Arranger.FULL_LIST}?page=${page}&pagesize=${PAGE_SIZE}&clId=${clientId}&arId=${arId}&arName=${arName}&dateAdded=${dateAdded}&lastUpdated=${lastUpdated}&order=${order}&isDesc=${isDesc}`
      );
    },
    {
      onError: () => {
        setToastResponse({error: 'Error loading arrangers'});
      },
      keepPreviousData: true,
    }
  );

  const {data: surveys} = useQuery(
    ['surveys', selectedArrangerId],
    async () => {
      const allServiceTypes = Object.values(ServiceTypes).reduce((acc, curr) => acc + curr, 0);
      return get(`${Survey.LIST}?pageSize=1000&serviceType=${allServiceTypes}&arrangerId=${selectedArrangerId}`);
    },
    {
      enabled: selectedArrangerId != null,
    }
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: {errors},
  } = useForm();

  const {
    fields: aliases,
    append: appendAlias,
    remove: removeAlias,
  } = useFieldArray({
    name: 'aliases',
    control,
  });

  const currentArrangerId = watch('id');

  const handleActionClick = (index: number) => {
    reset(arrangers?.data[index]);
    onOpen();
  };

  const handleAddArranger = () => {
    reset({
      id: null,
      clientId: '',
      arId: '',
      arName: '',
      arInact: false,
      aliases: [],
    });
    onOpen();
  };

  const handleViewSurveys = (arrangerId: string) => {
    setSelectedArrangerId(arrangerId);
    onSurveysOpen();
  };

  const onSaveArranger = async arranger => {
    const id = arranger.id;

    if (id === null) {
      addArranger(arranger);
    } else {
      updateArranger(arranger);
    }
  };

  const addArranger = async arranger => {
    const ar = {
      aliases: arranger.aliases,
      clientId: arranger.clientId,
      arName: arranger.arName,
      arInact: arranger.arInact,
    };
    const response = await post(Arranger.ADD, ar);

    if (!response.isSuccess) {
      setToastResponse({response: response, errorMessage: response.error});
      return;
    }

    queryClient.invalidateQueries('arrangers');
    setToastResponse({response: response, successMessage: 'Successfully added ' + ar.arName});
    onClose();
    setPage(1);
  };

  const updateArranger = async arranger => {
    const response = await post(Arranger.UPDATE + '/' + arranger?.id, arranger);

    if (!response.isSuccess) {
      setToastResponse({response: response, errorMessage: response.error});
      return;
    }

    queryClient.invalidateQueries('arrangers');
    setToastResponse({response: response, successMessage: 'Successfully updated ' + arranger?.arName});
    onClose();
  };

  const deleteArranger = async (index: number) => {
    const arranger = arrangers?.data[index];
    if (confirm('Are you sure you want to remove ' + arranger?.arName + '?')) {
      const response = await postDelete(Arranger.DELETE + '/' + arranger?.id);

      if (!response.isSuccess) {
        setToastResponse({response: response, errorMessage: response.error});
        return;
      }

      queryClient.invalidateQueries('arrangers');
      setToastResponse({response: response, successMessage: 'Successfully deleted ' + arranger?.arName});
    }
  };

  useEffect(() => {
    if (isAdmin()) setIsSalesMan(false);

    setTitle('Arranger Maintenance');
  }, []);

  useEffect(() => {
    sessionStorage.setItem('arranger-page', page.toString());
  }, [page]);

  useEffect(() => {
    sessionStorage.setItem('arranger-survey-open', isSurveysOpen ? 'true' : '');
  }, [isSurveysOpen]);

  useEffect(() => {
    sessionStorage.setItem('arranger-selectedArrangerId', selectedArrangerId ?? '');
  }, [selectedArrangerId]);

  const TableHeading = ({label, value}: {label: string; value: string}) => {
    const isOrder = order === value;

    const handleHeadingClick = () => {
      if (order === value && !isDesc) {
        setIsDesc(true);
        return;
      }

      if (order === value && isDesc) {
        setOrder('');
        setIsDesc(false);
        return;
      }

      setOrder(value);
      setIsDesc(false);
    };

    return (
      <Flex gap={3} cursor="pointer" onClick={handleHeadingClick}>
        <Text>{label}</Text>
        <Icon
          as={isOrder && isDesc ? FaChevronDown : FaChevronUp}
          opacity={isOrder ? 1 : 0.3}
          color={isOrder ? 'tertiary.600' : undefined}
          boxSize="15px"
        />
      </Flex>
    );
  };

  return (
    <Box mr="2">
      <BoxCard>
        <TableContainer position="relative" minH="200px">
          <Center
            position="absolute"
            left="50%"
            top="45%"
            bgColor="whiteAlpha.900"
            padding={3}
            borderRadius={8}
            opacity={isFetching ? 1 : 0}
            visibility={isFetching ? 'visible' : 'hidden'}
            transition="opacity 500ms ease-in"
            transitionDelay="150ms"
          >
            <Spinner speed="700ms" />
          </Center>
          <Table border="solid 2px #0c5f78" variant="striped" colorScheme="mint" size={'md'}>
            <Thead>
              <Tr border="solid 2px #0c5f78">
                <Th />
                <Th>
                  <Input
                    borderColor="gray.300"
                    onChange={debounce(event => {
                      setPage(1);
                      setClientId(event.target.value), DEBOUNCE_TIME;

                      sessionStorage.setItem('arranger-clientId', event.target.value);
                    })}
                    defaultValue={sessionStorage.getItem('arranger-clientId') ?? ''}
                  />
                </Th>
                <Th>
                  <Input
                    borderColor="gray.300"
                    onChange={debounce(event => {
                      setPage(1);
                      setArId(event.target.value), DEBOUNCE_TIME;

                      sessionStorage.setItem('arranger-arId', event.target.value);
                    })}
                    defaultValue={sessionStorage.getItem('arranger-arId') ?? ''}
                  />
                </Th>
                <Th>
                  <Input
                    borderColor="gray.300"
                    onChange={debounce(event => {
                      setPage(1);
                      setArName(event.target.value), DEBOUNCE_TIME;

                      sessionStorage.setItem('arranger-arName', event.target.value);
                    })}
                    defaultValue={sessionStorage.getItem('arranger-arName') ?? ''}
                  />
                </Th>
                <Th>
                  <Input
                    borderColor="gray.300"
                    type="date"
                    onChange={event => {
                      setPage(1);
                      setDateAdded(event.target.value);

                      sessionStorage.setItem('arranger-dateAdded', event.target.value);
                    }}
                    defaultValue={sessionStorage.getItem('arranger-dateAdded') ?? ''}
                  />
                </Th>
                <Th>
                  <Input
                    borderColor="gray.300"
                    type="date"
                    onChange={event => {
                      setPage(1);
                      setLastUpdated(event.target.value);

                      sessionStorage.setItem('arranger-lastUpdated', event.target.value);
                    }}
                    defaultValue={sessionStorage.getItem('arranger-lastUpdated') ?? ''}
                  />
                </Th>
              </Tr>
              <Tr border="solid 2px #0c5f78">
                <Td>
                  {!isSalesMan ? (
                    <Button
                      colorScheme="tertiary"
                      title="Add"
                      size="md"
                      leftIcon={<FaPlusSquare />}
                      onClick={() => handleAddArranger()}
                      aria-label="Add Arranger"
                    >
                      Add Arranger
                    </Button>
                  ) : null}
                </Td>
                <Th>
                  <TableHeading label="Client Id" value="Nclientpk" />
                </Th>
                <Th>
                  <TableHeading label="Arranger Id" value="ArId" />
                </Th>
                <Th>
                  <TableHeading label="Arranger Name" value="ArName" />
                </Th>
                <Th>
                  <TableHeading label="Date Added" value="DateAdded" />
                </Th>
                <Th>
                  <TableHeading label="Last Updated" value="LastUpdated" />
                </Th>
                <Th>
                  <Text>Inactive</Text>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {arrangers?.data?.map((arranger, index) => {
                return (
                  <Tr key={arranger.id}>
                    <Td>
                      <Button colorScheme="secondary" onClick={() => handleViewSurveys(arranger.id)} mr={3}>
                        Surveys
                      </Button>
                      <Button colorScheme="tertiary" onClick={() => handleActionClick(index)} mr={3}>
                        {isSalesMan ? 'View' : 'Edit'}
                      </Button>
                      {!isSalesMan && (
                        <IconButton
                          onClick={() => deleteArranger(index)}
                          colorScheme="red"
                          title="Delete"
                          size="md"
                          icon={<FaTrashAlt />}
                          aria-label="Delete"
                        />
                      )}
                    </Td>
                    <Td>{arranger.clId}</Td>
                    <Td>{arranger.arId}</Td>
                    <Td>{arranger.arName}</Td>
                    <Td>{formatDate(arranger.dateAdded)}</Td>
                    <Td>{formatDate(arranger.lastUpdated)}</Td>
                    <Td>
                      <Checkbox colorScheme="tertiary" isChecked={arranger.arInact} isReadOnly />
                    </Td>
                  </Tr>
                );
              })}
              {Array(PAGE_SIZE - (arrangers?.data?.length ?? 0))
                .fill(0)
                .map((_, index) => (
                  <Tr key={index}>
                    <Td>&nbsp;</Td>
                    <Td></Td>
                    <Td></Td>
                    <Td></Td>
                    <Td></Td>
                    <Td></Td>
                    <Td></Td>
                  </Tr>
                ))}
            </Tbody>
            <Tfoot></Tfoot>
          </Table>
          <Pagination page={page - 1} pageCount={arrangers?.pageCount ?? 0} onPageChange={({selected}) => setPage(selected + 1)} />
        </TableContainer>
      </BoxCard>
      <Modal blockScrollOnMount={false} size={'4xl'} isOpen={isOpen} onClose={onClose}>
        <form onSubmit={handleSubmit(onSaveArranger)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader id="header">
              <Flex>
                {isSalesMan ? 'View Arranger' : 'Edit Arranger'}
                {!isSalesMan && (
                  <Button colorScheme="tertiary" ml="5" type="submit">
                    Save
                  </Button>
                )}
              </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Stack>
                <CustomInput id="id" register={register} type="hidden" />
                {currentArrangerId != null && <CustomInput id="arId" register={register} name="Arranger Id" isReadOnly />}
                <CustomInput id="arName" register={register} name="Arranger Name" errors={errors} required isReadOnly={isSalesMan} />
                <ClientSelectInput control={control} name="clientId" required isReadOnly={isSalesMan} />
                <CustomCheckbox id="arInact" control={control} name="Inactive" isReadOnly={isSalesMan} />
              </Stack>

              <Divider mt="5" colorScheme={'mint'} width={{base: '100%'}} />
              <TableContainer width={{base: '100%'}}>
                <Table variant="striped" border="solid 2px #0c5f78" colorScheme="mint" size={'md'}>
                  <Thead>
                    <Tr border="solid 2px #0c5f78">
                      <Th>Alias Number</Th>
                      <Th>Alias</Th>
                      <Td>
                        {!isSalesMan && (
                          <Button
                            colorScheme="tertiary"
                            title="Add"
                            size="md"
                            leftIcon={<FaPlusSquare />}
                            onClick={() => appendAlias({})}
                            aria-label="Add Alias"
                          >
                            Add Alias
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {aliases.map((alias, index) => {
                      return (
                        <Tr key={alias.id}>
                          <Td>{index + 1}</Td>
                          <Td>
                            <Box padding={2}>
                              {isSalesMan ? (
                                alias.calias
                              ) : (
                                <CustomInput id={`aliases.${index}.calias`} register={register} errors={errors} required />
                              )}
                            </Box>
                          </Td>
                          <Td>
                            {!isSalesMan && (
                              <IconButton
                                colorScheme="tertiary"
                                title="Delete"
                                size="md"
                                icon={<FaTrashAlt />}
                                onClick={() => removeAlias(index)}
                                aria-label="Delete Alias"
                              />
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </ModalBody>

            <ModalFooter></ModalFooter>
          </ModalContent>
        </form>
      </Modal>

      <SurveyTableModal filterName="arrangerId" filterId={selectedArrangerId} isOpen={isSurveysOpen} onClose={onSurveysClose} />

      <ErrorHelper toastResponse={toastResponse} />
    </Box>
  );
}
